import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowUpDown,
  BarChart3,
  ChevronRight,
  Clock,
  Flag,
  HelpCircle,
  Search,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { AdPlacement } from "@/components/ads/AdPlacement";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { m } from "@/paraglide/messages";
import { F1Explainer } from "../components/F1Explainer";
import { SeasonRaceSelector } from "../components/SeasonRaceSelector";
import { StatisticsTableSkeleton } from "../components/StatisticsSkeletons";
import { useRaces, useSeasons } from "../hooks/use-f1-data";
import { useStatisticsLeaderboard } from "../hooks/use-statistics";
import { DEFAULT_SEASON } from "../lib/constants";
import { getTeamColor } from "../lib/team-colors";

const routeApi = getRouteApi("/f1-fantasy/statistics/");
const FANTASY_MIN_SEASON = 2023;

type SortField = string;
type SortDir = "asc" | "desc";

function SortButton({
  field,
  label,
  sortBy,
  onSort,
}: {
  field: string;
  label: string;
  sortBy: string;
  onSort: (field: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {label}
      <ArrowUpDown
        className={`h-3 w-3 ${sortBy === field ? "text-primary" : ""}`}
      />
    </button>
  );
}

export function Statistics() {
  const loaderData = routeApi.useLoaderData();
  const navigate = useNavigate({ from: "/f1-fantasy/statistics/" });
  const {
    tab = "race_results",
    entity = "drivers",
    sort,
    dir,
  } = routeApi.useSearch();

  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Sort is URL-driven so it survives refresh/share; falls back to points desc.
  const sortBy: SortField = sort ?? "totalPoints";
  const sortDir: SortDir = dir ?? "desc";
  const [selectedSeason, setSelectedSeason] = useState<number>(
    loaderData?.season ?? DEFAULT_SEASON,
  );

  const { data: seasons } = useSeasons();
  const { data: races } = useRaces(selectedSeason);
  // Capture current time once at mount — stable reference for filtering past races
  const [now] = useState(() => Date.now());
  const lastRace = useMemo(() => {
    if (!races) return null;
    const past = races.filter(
      (r) => r.date && new Date(r.date).getTime() < now,
    );
    return past.length > 0 ? past[past.length - 1] : null;
  }, [races, now]);

  // Clamp season for fantasy tab
  const effectiveSeason =
    tab === "fantasy_stats" && selectedSeason < FANTASY_MIN_SEASON
      ? FANTASY_MIN_SEASON
      : selectedSeason;

  const { data, isLoading } = useStatisticsLeaderboard(
    tab,
    entity,
    effectiveSeason,
  );

  const showLoading = isLoading && !data;

  // Available seasons for selector — only show seasons with fantasy data
  const availableSeasons = useMemo(() => {
    if (!seasons) return [];
    return seasons.filter((s) => s.year >= FANTASY_MIN_SEASON);
  }, [seasons]);

  // Handle sort — persists to the URL (toggles direction on the active field).
  const handleSort = (field: SortField) => {
    const nextDir: SortDir =
      sortBy === field ? (sortDir === "asc" ? "desc" : "asc") : "desc";
    navigate({
      search: (prev) => ({ ...prev, sort: field, dir: nextDir }),
      replace: true,
    });
  };

  // Get current rows based on tab + entity
  const rows = useMemo(() => {
    if (!data) return [];

    let items: Record<string, unknown>[] = [];
    if (tab === "race_results" && entity === "drivers") {
      items = data.driverRows;
    } else if (tab === "race_results" && entity === "constructors") {
      items = data.constructorRows;
    } else if (tab === "fantasy_stats" && entity === "drivers") {
      items = data.driverFantasyRows;
    } else {
      items = data.constructorFantasyRows;
    }

    // Filter by search
    const query = searchQuery.toLowerCase();
    let filtered = items;
    if (query) {
      filtered = items.filter((row) => {
        const name =
          (row.driverFullName as string) ??
          (row.constructorName as string) ??
          "";
        const team = (row.constructorName as string) ?? "";
        return (
          name.toLowerCase().includes(query) ||
          team.toLowerCase().includes(query)
        );
      });
    }

    // Sort
    return [...filtered].sort((a, b) => {
      const aVal = (a[sortBy] as number) ?? 0;
      const bVal = (b[sortBy] as number) ?? 0;
      return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });
  }, [data, tab, entity, searchQuery, sortBy, sortDir]);

  return (
    <div>
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4 space-y-3 md:space-y-0">
          {/* Row 1: title + (desktop: all controls) */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/f1-fantasy"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">{m.common_back()}</span>
              </Link>
              <div className="w-px h-6 bg-border" />
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <h1 className="font-display text-xl font-bold text-foreground">
                  {m.f1_stats_title()}
                </h1>
              </div>
            </div>

            {/* Mobile search icon — hidden on desktop */}
            <button
              type="button"
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              aria-label={
                mobileSearchOpen
                  ? m.common_close_search()
                  : m.common_open_search()
              }
              aria-expanded={mobileSearchOpen}
              className={`md:hidden p-2 rounded-lg transition-colors ${
                mobileSearchOpen
                  ? "bg-primary/20 border border-primary/50 text-primary"
                  : "chip-idle text-foreground/70"
              }`}
            >
              <Search className="h-4 w-4" aria-hidden="true" />
            </button>

            {/* Desktop controls */}
            <div className="hidden md:flex items-center gap-3 flex-wrap">
              {/* Tab: Race Results | Fantasy Stats */}
              <div className="flex rounded-lg tab-group p-0.5">
                <button
                  type="button"
                  onClick={() =>
                    navigate({ search: { tab: "race_results", entity } })
                  }
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    tab === "race_results"
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {m.f1_stats_tab_race_results()}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate({ search: { tab: "fantasy_stats", entity } })
                  }
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    tab === "fantasy_stats"
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {m.f1_stats_tab_fantasy()}
                </button>
              </div>

              <div className="w-px h-6 bg-border" />

              {/* Tab: Drivers | Constructors */}
              <div className="flex rounded-lg tab-group p-0.5">
                <button
                  type="button"
                  onClick={() =>
                    navigate({ search: { tab, entity: "drivers" } })
                  }
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    entity === "drivers"
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  <Users className="h-4 w-4" />
                  {m.f1_standings_tab_drivers()}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate({ search: { tab, entity: "constructors" } })
                  }
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    entity === "constructors"
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  <Flag className="h-4 w-4" />
                  {m.f1_standings_tab_constructors()}
                </button>
              </div>

              {/* Season Selector */}
              <SeasonRaceSelector
                key={`stats-desktop-${effectiveSeason}`}
                defaultSeason={effectiveSeason}
                onSeasonChange={setSelectedSeason}
                showRaceSelector={false}
                filterSeasons={availableSeasons.map((s) => s.year)}
              />

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={m.f1_stats_search()}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 w-48 rounded-lg bg-muted/50 border border-border text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
          </div>

          {/* Mobile controls */}
          <div className="md:hidden space-y-2">
            {/* Expandable search — immediately below title row */}
            {mobileSearchOpen && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={m.f1_stats_search()}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 rounded-lg bg-muted/50 border border-border text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label={m.common_clear_search()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            {/* Year selector */}
            <SeasonRaceSelector
              key={`stats-mobile-${effectiveSeason}`}
              defaultSeason={effectiveSeason}
              onSeasonChange={setSelectedSeason}
              showRaceSelector={false}
              filterSeasons={availableSeasons.map((s) => s.year)}
              compact
            />

            {/* Row 1: Race Results | Fantasy Stats */}
            <div className="flex rounded-lg tab-group p-0.5">
              <button
                type="button"
                onClick={() =>
                  navigate({ search: { tab: "race_results", entity } })
                }
                className={`flex-1 flex items-center justify-center px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                  tab === "race_results"
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                {m.f1_stats_tab_race_results()}
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate({ search: { tab: "fantasy_stats", entity } })
                }
                className={`flex-1 flex items-center justify-center px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                  tab === "fantasy_stats"
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                {m.f1_stats_tab_fantasy()}
              </button>
            </div>

            {/* Row 2: Drivers | Constructors */}
            <div className="flex rounded-lg tab-group p-0.5">
              <button
                type="button"
                onClick={() => navigate({ search: { tab, entity: "drivers" } })}
                className={`flex-1 flex items-center justify-center gap-1 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                  entity === "drivers"
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                <Users className="h-3.5 w-3.5" />
                {m.f1_standings_tab_drivers()}
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate({ search: { tab, entity: "constructors" } })
                }
                className={`flex-1 flex items-center justify-center gap-1 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                  entity === "constructors"
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                <Flag className="h-3.5 w-3.5" />
                {m.f1_standings_tab_constructors()}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Intro + Data Freshness */}
        <div className="mb-5">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {m.f1_stats_intro({ season: effectiveSeason })}
          </p>
          {lastRace && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {m.f1_stats_data_through({ name: lastRace.name })}
            </p>
          )}
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors mt-2"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                {m.f1_stats_how_it_works()}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{m.f1_stats_how_title()}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-foreground/80">
                <p>{m.f1_stats_how_p1()}</p>
                <p>{m.f1_stats_how_p2()}</p>
                <p>{m.f1_stats_how_p3()}</p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <AdPlacement className="my-6" />

        {/* Loading State */}
        {showLoading && <StatisticsTableSkeleton rows={20} />}

        {/* Data Table */}
        {!showLoading && (
          <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    {tab === "race_results" && entity === "drivers" && (
                      <>
                        <th>{m.f1_standings_col_driver()}</th>
                        <th>{m.f1_standings_col_team()}</th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="totalPoints"
                            label={m.f1_standings_col_points()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgQualifyingPosition"
                            label={m.f1_stats_col_avg_quali()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgFinishPosition"
                            label={m.f1_stats_col_avg_finish()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="totalWins"
                            label={m.f1_standings_col_wins()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="podiumRate"
                            label={m.f1_stats_col_podium_pct()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="dnfRate"
                            label={m.f1_stats_col_dnf_pct()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgPositionsGained"
                            label={m.f1_stats_col_pos_gained()}
                          />
                        </th>
                        <th title="Fastest Lap award">{m.f1_stats_col_fl()}</th>
                        <th className="w-8">
                          <span className="sr-only">
                            {m.f1_stats_col_details()}
                          </span>
                        </th>
                      </>
                    )}
                    {tab === "race_results" && entity === "constructors" && (
                      <>
                        <th>{m.f1_stats_col_constructor()}</th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgFinishPosition"
                            label={m.f1_stats_col_avg_finish()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="totalPoints"
                            label={m.f1_standings_col_points()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="totalWins"
                            label={m.f1_standings_col_wins()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="podiumRate"
                            label={m.f1_stats_col_podium_pct()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="dnfRate"
                            label={m.f1_stats_col_dnf_pct()}
                          />
                        </th>
                        <th className="w-8">
                          <span className="sr-only">
                            {m.f1_stats_col_details()}
                          </span>
                        </th>
                      </>
                    )}
                    {tab === "fantasy_stats" && entity === "drivers" && (
                      <>
                        <th>{m.f1_standings_col_driver()}</th>
                        <th>{m.f1_standings_col_team()}</th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="totalPoints"
                            label={m.f1_stats_col_total_pts()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgFantasyPoints"
                            label={m.f1_stats_col_avg_pts()}
                          />
                        </th>
                        <th title="Points per million spent">
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgPPM"
                            label={m.f1_stats_col_ppm()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="currentPrice"
                            label={m.f1_stats_col_price()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="priceTrend"
                            label={m.f1_stats_col_trend()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgOvertakes"
                            label={m.f1_stats_col_ot_race()}
                          />
                        </th>
                        <th title="Driver of the Day">
                          {m.f1_stats_col_dotd()}
                        </th>
                        <th title="Fastest Lap award">{m.f1_stats_col_fl()}</th>
                        <th className="w-8">
                          <span className="sr-only">
                            {m.f1_stats_col_details()}
                          </span>
                        </th>
                      </>
                    )}
                    {tab === "fantasy_stats" && entity === "constructors" && (
                      <>
                        <th>{m.f1_stats_col_constructor()}</th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="totalPoints"
                            label={m.f1_stats_col_total_pts()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgFantasyPoints"
                            label={m.f1_stats_col_avg_pts()}
                          />
                        </th>
                        <th title="Points per million spent">
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgPPM"
                            label={m.f1_stats_col_ppm()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="currentPrice"
                            label={m.f1_stats_col_price()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="priceTrend"
                            label={m.f1_stats_col_trend()}
                          />
                        </th>
                        <th>
                          <SortButton
                            sortBy={sortBy}
                            onSort={handleSort}
                            field="avgPitStopPoints"
                            label={m.f1_stats_col_pit_pts()}
                          />
                        </th>
                        <th className="w-8">
                          <span className="sr-only">
                            {m.f1_stats_col_details()}
                          </span>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <DesktopRow
                      key={(row.id as string) ?? i}
                      row={row}
                      tab={tab}
                      entity={entity}
                      index={i}
                      season={effectiveSeason}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <AdPlacement className="my-6" />

            {/* Mobile Sort Control */}
            <div className="md:hidden flex items-center gap-2 px-4 py-2 border-b border-border">
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="flex items-center gap-1.5 overflow-x-auto flex-1 scrollbar-hide">
                {(tab === "race_results" && entity === "drivers"
                  ? [
                      { field: "totalPoints", label: m.f1_stats_sort_points() },
                      {
                        field: "avgQualifyingPosition",
                        label: m.f1_stats_sort_avg_quali(),
                      },
                      {
                        field: "avgFinishPosition",
                        label: m.f1_stats_sort_avg_finish(),
                      },
                    ]
                  : tab === "race_results"
                    ? [
                        {
                          field: "totalPoints",
                          label: m.f1_stats_sort_points(),
                        },
                        {
                          field: "avgFinishPosition",
                          label: m.f1_stats_sort_avg_finish(),
                        },
                      ]
                    : entity === "drivers"
                      ? [
                          {
                            field: "totalPoints",
                            label: m.f1_stats_sort_points(),
                          },
                          { field: "avgPPM", label: m.f1_stats_sort_ppm() },
                          {
                            field: "avgFantasyPoints",
                            label: m.f1_stats_sort_avg_pts(),
                          },
                          {
                            field: "currentPrice",
                            label: m.f1_stats_sort_price(),
                          },
                        ]
                      : [
                          {
                            field: "totalPoints",
                            label: m.f1_stats_sort_points(),
                          },
                          { field: "avgPPM", label: m.f1_stats_sort_ppm() },
                          {
                            field: "avgFantasyPoints",
                            label: m.f1_stats_sort_avg_pts(),
                          },
                          {
                            field: "currentPrice",
                            label: m.f1_stats_sort_price(),
                          },
                        ]
                ).map((opt) => (
                  <button
                    key={opt.field}
                    type="button"
                    onClick={() => handleSort(opt.field)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                      sortBy === opt.field
                        ? "chip-on"
                        : "chip-idle text-foreground/70"
                    }`}
                  >
                    {opt.label}
                    {sortBy === opt.field && (
                      <span className="ml-1 opacity-70">
                        {sortDir === "desc" ? "↓" : "↑"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-border">
              {rows.map((row, i) => (
                <MobileCard
                  key={(row.id as string) ?? i}
                  row={row}
                  tab={tab}
                  entity={entity}
                  index={i}
                  season={effectiveSeason}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!showLoading && rows.length === 0 && (
          <div className="text-center py-16">
            <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? m.f1_stats_no_results() : m.f1_stats_no_data()}
            </p>
          </div>
        )}

        {/* Season info */}
        {!showLoading && rows.length > 0 && (
          <div className="mt-6 text-center text-sm text-muted-foreground">
            {tab === "race_results"
              ? m.f1_stats_showing_race({
                  season: effectiveSeason,
                  count: rows.length,
                  entity,
                })
              : m.f1_stats_showing_fantasy({
                  season: effectiveSeason,
                  count: rows.length,
                  entity,
                })}
          </div>
        )}
      </div>

      <F1Explainer
        title={m.f1_stx_title()}
        howTo={m.f1_stx_howto()}
        faqs={[
          { q: m.f1_stx_faq1_q(), a: m.f1_stx_faq1_a() },
          { q: m.f1_stx_faq2_q(), a: m.f1_stx_faq2_a() },
          { q: m.f1_stx_faq3_q(), a: m.f1_stx_faq3_a() },
          { q: m.f1_stx_faq4_q(), a: m.f1_stx_faq4_a() },
        ]}
      />
      <AdPlacement className="mt-6" />
    </div>
  );
}

// ============================================================================
// Desktop Row
// ============================================================================

function DesktopRow({
  row,
  tab,
  entity,
  index,
  season,
}: {
  row: Record<string, unknown>;
  tab: string;
  entity: string;
  index: number;
  season: number;
}) {
  const navigate = useNavigate();
  const teamColor = getTeamColor((row.constructorSlug as string) ?? "");

  const isDriver = entity === "drivers";
  const slug = isDriver
    ? (row.driverSlug as string)
    : (row.constructorSlug as string);
  const profileTo = isDriver
    ? "/f1-fantasy/statistics/drivers/$slug"
    : "/f1-fantasy/statistics/constructors/$slug";

  const name = isDriver
    ? (row.driverFullName as string)
    : (row.constructorName as string);

  return (
    <tr
      className="hover:bg-primary/5 transition-colors cursor-pointer"
      style={{ animationDelay: `${Math.min(index, 10) * 50}ms` }}
      onClick={() =>
        navigate({
          to: profileTo,
          params: { slug },
          search: {
            season,
            context: tab === "race_results" ? "race" : "fantasy",
          },
        })
      }
    >
      {tab === "race_results" && entity === "drivers" && (
        <>
          <td>
            <div className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-right font-mono text-xs text-muted-foreground tabular-nums">
                {index + 1}
              </span>
              <div
                className="w-1 h-8 rounded-full shrink-0"
                style={{ backgroundColor: teamColor }}
              />
              <span className="font-medium">{name}</span>
            </div>
          </td>
          <td className="text-muted-foreground text-sm">
            {row.constructorName as string}
          </td>
          <td className="font-mono font-medium text-primary">
            {row.totalPoints as number}
          </td>
          <td className="font-mono text-sm">
            {row.avgQualifyingPosition != null
              ? `P${row.avgQualifyingPosition}`
              : "—"}
          </td>
          <td className="font-mono text-sm">
            {row.avgFinishPosition != null ? `P${row.avgFinishPosition}` : "—"}
          </td>
          <td className="font-mono">{row.totalWins as number}</td>
          <td className="font-mono text-sm">{row.podiumRate as number}%</td>
          <td className="font-mono text-sm">{row.dnfRate as number}%</td>
          <td className="font-mono text-sm">
            {(row.avgPositionsGained as number) > 0 ? "+" : ""}
            {row.avgPositionsGained as number}
          </td>
          <td className="font-mono text-sm">{row.fastestLapCount as number}</td>
          <td className="w-8 text-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground inline" />
          </td>
        </>
      )}
      {tab === "race_results" && entity === "constructors" && (
        <>
          <td>
            <div className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-right font-mono text-xs text-muted-foreground tabular-nums">
                {index + 1}
              </span>
              <div
                className="w-1 h-8 rounded-full shrink-0"
                style={{ backgroundColor: teamColor }}
              />
              <span className="font-medium">{name}</span>
            </div>
          </td>
          <td className="font-mono text-sm">
            {row.avgFinishPosition != null ? `P${row.avgFinishPosition}` : "—"}
          </td>
          <td className="font-mono font-medium text-primary">
            {row.totalPoints as number}
          </td>
          <td className="font-mono">{row.totalWins as number}</td>
          <td className="font-mono text-sm">{row.podiumRate as number}%</td>
          <td className="font-mono text-sm">{row.dnfRate as number}%</td>
          <td className="w-8 text-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground inline" />
          </td>
        </>
      )}
      {tab === "fantasy_stats" && entity === "drivers" && (
        <>
          <td>
            <div className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-right font-mono text-xs text-muted-foreground tabular-nums">
                {index + 1}
              </span>
              <div
                className="w-1 h-8 rounded-full shrink-0"
                style={{ backgroundColor: teamColor }}
              />
              <span className="font-medium">{name}</span>
            </div>
          </td>
          <td className="text-muted-foreground text-sm">
            {row.constructorName as string}
          </td>
          <td className="font-mono font-medium text-primary">
            {row.totalPoints as number}
          </td>
          <td className="font-mono text-sm">
            {row.avgFantasyPoints as number}
          </td>
          <td className="font-mono text-sm">{row.avgPPM as number}</td>
          <td className="font-mono text-sm">${row.currentPrice as number}M</td>
          <td className="font-mono text-sm">
            <span
              className={
                (row.priceTrend as number) > 0
                  ? "text-success"
                  : (row.priceTrend as number) < 0
                    ? "text-destructive"
                    : ""
              }
            >
              {(row.priceTrend as number) > 0 ? "+" : ""}
              {row.priceTrend as number}
            </span>
          </td>
          <td className="font-mono text-sm">{row.avgOvertakes as number}</td>
          <td className="font-mono text-sm">{row.dotdCount as number}</td>
          <td className="font-mono text-sm">{row.fastestLapCount as number}</td>
          <td className="w-8 text-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground inline" />
          </td>
        </>
      )}
      {tab === "fantasy_stats" && entity === "constructors" && (
        <>
          <td>
            <div className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-right font-mono text-xs text-muted-foreground tabular-nums">
                {index + 1}
              </span>
              <div
                className="w-1 h-8 rounded-full shrink-0"
                style={{ backgroundColor: teamColor }}
              />
              <span className="font-medium">{name}</span>
            </div>
          </td>
          <td className="font-mono font-medium text-primary">
            {row.totalPoints as number}
          </td>
          <td className="font-mono text-sm">
            {row.avgFantasyPoints as number}
          </td>
          <td className="font-mono text-sm">{row.avgPPM as number}</td>
          <td className="font-mono text-sm">${row.currentPrice as number}M</td>
          <td className="font-mono text-sm">
            <span
              className={
                (row.priceTrend as number) > 0
                  ? "text-success"
                  : (row.priceTrend as number) < 0
                    ? "text-destructive"
                    : ""
              }
            >
              {(row.priceTrend as number) > 0 ? "+" : ""}
              {row.priceTrend as number}
            </span>
          </td>
          <td className="font-mono text-sm">
            {row.avgPitStopPoints as number}
          </td>
          <td className="w-8 text-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground inline" />
          </td>
        </>
      )}
    </tr>
  );
}

// ============================================================================
// Mobile Card
// ============================================================================

function MobileCard({
  row,
  tab,
  entity,
  index,
  season,
}: {
  row: Record<string, unknown>;
  tab: string;
  entity: string;
  index: number;
  season: number;
}) {
  const teamColor = getTeamColor((row.constructorSlug as string) ?? "");

  const isDriver = entity === "drivers";
  const slug = isDriver
    ? (row.driverSlug as string)
    : (row.constructorSlug as string);
  const profileTo = isDriver
    ? "/f1-fantasy/statistics/drivers/$slug"
    : "/f1-fantasy/statistics/constructors/$slug";

  const name = isDriver
    ? (row.driverFullName as string)
    : (row.constructorName as string);

  const primaryStat = m.f1_stats_pill_pts({
    value: String(row.totalPoints ?? 0),
  });

  // Mobile metric pills — kept close to desktop columns so phones aren't
  // starved of the data the table shows.
  const pills: string[] = [];
  if (tab === "race_results" && isDriver) {
    if (row.avgFinishPosition != null)
      pills.push(
        m.f1_stats_pill_finish({ value: String(row.avgFinishPosition) }),
      );
    if (row.avgPositionsGained != null) {
      const pg = row.avgPositionsGained as number;
      pills.push(m.f1_stats_pill_pos({ value: `${pg > 0 ? "+" : ""}${pg}` }));
    }
    if (row.podiumRate != null)
      pills.push(m.f1_stats_pill_podium({ value: String(row.podiumRate) }));
    if ((row.totalWins as number) > 0)
      pills.push(m.f1_stats_pill_wins({ count: String(row.totalWins) }));
    if (row.dnfRate != null)
      pills.push(m.f1_stats_pill_dnf({ value: String(row.dnfRate) }));
  } else if (tab === "fantasy_stats" && isDriver) {
    pills.push(m.f1_stats_pill_ppm({ value: String(row.avgPPM ?? 0) }));
    pills.push(`$${row.currentPrice ?? 0}M`);
    if (row.avgOvertakes != null)
      pills.push(m.f1_stats_pill_ov({ value: String(row.avgOvertakes) }));
    if ((row.fastestLapCount as number) > 0)
      pills.push(m.f1_stats_pill_fl({ value: String(row.fastestLapCount) }));
    if ((row.dotdCount as number) > 0)
      pills.push(m.f1_stats_pill_dotd({ value: String(row.dotdCount) }));
  } else if (tab === "race_results" && !isDriver) {
    if (row.avgFinishPosition != null)
      pills.push(
        m.f1_stats_pill_finish({ value: String(row.avgFinishPosition) }),
      );
    pills.push(m.f1_stats_pill_wins({ count: String(row.totalWins ?? 0) }));
    if (row.podiumRate != null)
      pills.push(m.f1_stats_pill_podium({ value: String(row.podiumRate) }));
    pills.push(m.f1_stats_pill_dnf({ value: String(row.dnfRate ?? 0) }));
  } else {
    // fantasy_stats + constructors
    pills.push(m.f1_stats_pill_ppm({ value: String(row.avgPPM ?? 0) }));
    pills.push(`$${row.currentPrice ?? 0}M`);
    if (row.avgPitStopPoints != null)
      pills.push(
        m.f1_stats_pill_pit_pts({ value: String(row.avgPitStopPoints) }),
      );
  }

  return (
    <Link
      to={profileTo}
      params={{ slug }}
      search={{ season, context: tab === "race_results" ? "race" : "fantasy" }}
      className="flex items-center gap-3 px-4 py-3 hover:bg-primary/5 active:scale-[0.99] transition-all"
      style={{ animationDelay: `${Math.min(index, 10) * 50}ms` }}
    >
      <span className="w-6 shrink-0 text-center font-mono text-sm font-semibold text-muted-foreground tabular-nums">
        {index + 1}
      </span>
      <div
        className="w-1 h-12 rounded-full shrink-0"
        style={{ backgroundColor: teamColor }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{name}</div>
        {isDriver && (
          <div className="text-xs text-muted-foreground truncate">
            {row.constructorName as string}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-1.5">
          {pills.map((pill) => (
            <span
              key={pill}
              className="rounded bg-muted/60 px-1.5 py-0.5 text-[11px] font-medium text-foreground/75"
            >
              {pill}
            </span>
          ))}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono font-bold text-primary text-sm">
          {primaryStat}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </Link>
  );
}
